import React from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, PageHeader, Button, Badge } from "@/components/ui";
import { Download, FileText, Target, Layers, Rocket, Building2, ShieldCheck, Map } from "lucide-react";

async function downloadPdf(key, name) {
  try {
    const res = await api.get(`/docs/${key}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = name; a.click();
    URL.revokeObjectURL(url);
  } catch { toast.error("PDF export failed"); }
}

const segments = [
  ["Tier 1 — Large MCO / Tailored Plan", "100k–1M+ lives", "Direct RFP sales, 9–12 mo"],
  ["Tier 2 — Mid-market Medicaid/BH", "25k–100k", "Editions, partner-assisted"],
  ["Tier 3 — Specialty / ACO", "<25k", "Self-service, land & expand"],
  ["Channel — TPA / BPO", "Volume", "White-label / OEM"],
];
const phases = [
  ["Discovery & Fit", "Wk 0–4", "Map plans, populations, contracts, security"],
  ["Configuration Sprint", "Wk 4–16", "Load content pack; configure workflows, plans, letters"],
  ["Integration", "Wk 12–24", "FHIR/HL7/X12, MDM, SFTP/REST"],
  ["Data Migration", "Wk 16–28", "Convert + reconcile with audit"],
  ["UAT & Go-Live", "Wk 24–40", "Train-the-trainer, phased cutover"],
  ["Hypercare", "Wk 40–52", "SLA monitoring, first quarterly release"],
];
const differentiators = [
  "Single data model, single platform — CM + UM + QM + billing + portals, no bolt-ons",
  "Configuration over customization — 80%+ of changes need zero vendor code",
  "Configurable AI native, not bolted on",
  "True data ownership + open, versioned API/SDK (no .bak/flat-file reliance)",
  "Whole-person depth: behavioral health, IDD, and TBI",
  "Onshore BPO option and transparent, penalty-backed SLAs",
];
const localization = [
  "State Medicaid program rules & contract obligations",
  "Security framework (NIST 800-53, state equivalents)",
  "Accreditation targets (NCQA, URAC)",
  "EDI companion guides (837/835/834/270-271)",
  "State-required screening/assessment libraries",
  "Benefit/plan definitions & rate structures",
  "Letter/notice templates & appeal language",
  "Interoperability targets (state HIE, FHIR)",
  "Subcontractor / FOCI disclosures (SCRM)",
];

function T({ head, rows }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">{head.map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}</tr></thead>
        <tbody className="divide-y divide-border">{rows.map((r, i) => <tr key={i} className="hover:bg-muted transition-colors">{r.map((c, j) => <td key={j} className={`px-4 py-3 ${j === 0 ? "font-medium text-foreground" : "text-muted-foreground"}`}>{c}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

export default function McoPlaybook() {
  return (
    <div className="vc-fade-up max-w-6xl">
      <PageHeader
        title="MCO Expansion Playbook"
        subtitle="How to replicate the Trillium solution for other Managed Care Organizations — productize once, configure many."
        actions={<div className="flex gap-2">
          <Button variant="outline" onClick={() => downloadPdf("mco-playbook", "GoMCO-MCO-Expansion-Playbook.pdf")} data-testid="download-playbook-pdf"><FileText className="h-4 w-4" /> PDF</Button>
          <a href="/docs/GoMCO-MCO-Expansion-Playbook.md" download><Button data-testid="download-playbook"><Download className="h-4 w-4" /> Markdown</Button></a>
        </div>}
      />

      <Card className="p-6 mb-6 bg-brand-950 text-white border-0">
        <div className="flex items-center gap-2 mb-3"><Target className="h-5 w-5 text-brand-300" /><h2 className="font-display text-xl font-bold">The Opportunity</h2></div>
        <p className="text-brand-100 leading-relaxed max-w-3xl">
          Trillium is one of dozens of comparable buyers — NC LME/MCOs, ~40 states of Medicaid managed care,
          behavioral-health & IDD specialty plans, D-SNPs, ACOs, and TPAs. The addressable market spans hundreds
          of risk-bearing entities and tens of millions of lives. Even 3–5% share is a <strong>$150M+ ARR</strong> business.
        </p>
      </Card>

      <Card className="p-6 mb-6">
        <div className="flex items-center gap-2 mb-2"><Layers className="h-5 w-5 text-brand-700" /><h2 className="font-display text-xl font-bold">Productize Once, Configure Many</h2></div>
        <p className="text-sm text-muted-foreground mb-4">Core principle: everything Trillium-specific is <strong>configuration or a content pack — never a code fork.</strong></p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            ["Multi-tenant architecture", "Per-tenant logical isolation; single-tenant option for large/regulated clients"],
            ["Configuration engine", "Rules, triggers, benefit plans, assessments, letters, RBAC — admin-configurable"],
            ["Content packs", "State/program packs (NC Tailored Plan, HEDIS, EDI guides) = new market, not new build"],
            ["Standards-first interoperability", "FHIR, HL7 v2/v3, X12 + configurable mapping engine"],
          ].map(([t, d]) => (
            <div key={t} className="rounded-md border border-border p-4"><p className="font-semibold text-sm">{t}</p><p className="text-sm text-muted-foreground mt-1">{d}</p></div>
          ))}
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4"><Building2 className="h-5 w-5 text-brand-700" /><h2 className="font-display text-xl font-bold">Ideal Customer Profile</h2></div>
          <T head={["Segment", "Size", "Motion"]} rows={segments} />
        </Card>
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4"><Rocket className="h-5 w-5 text-brand-700" /><h2 className="font-display text-xl font-bold">Repeatable Implementation</h2></div>
          <T head={["Phase", "Timing", "Scope"]} rows={phases} />
          <p className="text-xs text-muted-foreground mt-3">Compresses selection-to-go-live from 18+ months to <strong>9–12 months</strong>.</p>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4"><ShieldCheck className="h-5 w-5 text-brand-700" /><h2 className="font-display text-xl font-bold">Competitive Differentiators</h2></div>
          <ul className="space-y-2">{differentiators.map((d) => <li key={d} className="flex gap-2 text-sm text-muted-foreground"><span className="text-brand-600 font-bold">›</span>{d}</li>)}</ul>
          <p className="text-xs text-muted-foreground mt-4">vs. ZeOmega Jiva, HealthEdge GuidingCare, TriZetto, Medecision Essette, and homegrown/EHR-bolted tools.</p>
        </Card>
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4"><Map className="h-5 w-5 text-brand-700" /><h2 className="font-display text-xl font-bold">Localization Checklist (Per New MCO)</h2></div>
          <div className="grid grid-cols-1 gap-1.5">
            {localization.map((l) => <label key={l} className="flex items-center gap-2 text-sm text-muted-foreground"><input type="checkbox" className="rounded border-border accent-brand-700" /> {l}</label>)}
          </div>
        </Card>
      </div>

      <Card className="p-6">
        <h2 className="font-display text-xl font-bold mb-4">12-Month Expansion Roadmap</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            ["Q1", "Trillium go-live (Phase 1) + case-study capture"],
            ["Q2", "Publish NC Tailored-Plan pack; sign 1–2 adjacent NC LME/MCOs"],
            ["Q3", "Multi-tenant control plane + self-service admin; enter 1 new state"],
            ["Q4", "Partner-delivery program + marketplace beta; 3–5 tenants live"],
          ].map(([q, d]) => (
            <div key={q} className="rounded-md border border-border p-4">
              <Badge tone="Low">{q}</Badge><p className="text-sm text-muted-foreground mt-2">{d}</p>
            </div>
          ))}
        </div>
        <p className="text-sm text-muted-foreground mt-5"><strong className="text-brand-700">North Star:</strong> onboarding a new MCO becomes a configuration project, not a rebuild — turning one winning RFP into a repeatable, high-margin platform business.</p>
      </Card>
    </div>
  );
}
